import { inject, Injectable } from '@angular/core';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { HttpMainService } from '../http-main.service';
import { catchError, Observable, of, throwError } from 'rxjs';
import {
  CodeIntroducerResponseModel,
  CodeUpdateRequestModel, CreateIbInformationResponseModel, CreateIbRequestModel,
  IntroducerClientResponseModel,
  IntroducerResponseModel,
  IntroducerSupporterResponseModel,
  ListModel,
  ListRequestModel,
  ReferralsResponseModel
} from '@itb-monorepo/models';
import { APP_TOKEN, AppsEnum } from '@itb-monorepo/enums';
import { AuthService } from '../utility-services/auth.service';
import type { UseFor } from '@itb-monorepo/models';
import { IntroducerOptionsResponseModel } from '@itb-monorepo/models';
import { removeEmptyFields } from '@itb-monorepo/helper';
import { HTTP_ERROR_RESPONSE } from '../http-error-response.token';

@Injectable({
  providedIn: 'root',
})
export class IntroducersService {
  public app = inject(APP_TOKEN);
  private _HttpMainService = inject(HttpMainService);
  private authService = inject(AuthService);

  baseUrl = '/member/';

  current(userFor?: UseFor): Observable<IntroducerResponseModel> {
    if (!userFor) {
      userFor = 'IB';
    }
    const url =
      userFor == 'IB'
        ? 'v1/introducers/current'
        : 'v1/referrals/current?include=user';
    return this._HttpMainService.get(this.baseUrl + url);
  }

  getIntroducerList(
    params: ListRequestModel
  ): Observable<ListModel<IntroducerSupporterResponseModel>> {
    if (this.app == AppsEnum.SUPPORTER) {
      if (!this.authService.hasAccess('introducers:L')) return of();
    }
    params.include = ['user.profile', 'information'];
    params.sort = 'desc(created_at)';
    return this._HttpMainService.get(this.baseUrl + `v1/introducers`, params);
  }
  getIbListExcel(params: any): Observable<any> {
    if (!this.authService.hasAccess('introducers:L')) return of();
    const url = `${this.baseUrl}v1/introducers`;
    return this._HttpMainService.getFile(
      url,
      'application/csv',
      removeEmptyFields(params)
    );
  }

  getClient(userId: string): Observable<IntroducerClientResponseModel> {
    return this._HttpMainService.get(
      this.baseUrl + `v1/introducers/current/clients/${userId}`
    );
  }

  getReferralList(params: any): Observable<ListModel<ReferralsResponseModel>> {
    params.include = ['user'];
    params.sort = 'desc(created_at)';
    return this._HttpMainService.get(
      this.baseUrl + `v1/users/current/referrals`,
      params
    );
  }

  updateIntroducer(
    model: CodeUpdateRequestModel,
    userFor: UseFor
  ): Observable<CodeIntroducerResponseModel> {
    const url =
      userFor == 'IB' ? 'v1/introducers/current' : 'v1/referrals/current';
    return this._HttpMainService.put(this.baseUrl + url, model);
  }
  createIntroducerInformation(
    dataInformation: CreateIbRequestModel
  ): Observable<CreateIbInformationResponseModel> {
    return this.createIntroducerIfMissing({
      information: JSON.stringify(dataInformation),
    });
  }
  createIntroducer(): Observable<CreateIbInformationResponseModel> {
    return this.createIntroducerIfMissing(null);
  }
  private createIntroducerIfMissing(
    data: { information: string } | null
  ): Observable<CreateIbInformationResponseModel> {
    return this.getCurrentIntroducer().pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 404) {
          return throwError(() => error);
        }

        return this._HttpMainService
          .post<CreateIbInformationResponseModel>(
            `${this.baseUrl}v1/introducers`,
            data,
            undefined,
            new HttpContext().set(HTTP_ERROR_RESPONSE, true)
          )
          .pipe(
            catchError((createError: HttpErrorResponse) => {
              if (createError.status === 409) {
                return this.getCurrentIntroducer();
              }
              return throwError(() => createError);
            })
          );
      })
    );
  }
  getCurrentIntroducer(): Observable<CreateIbInformationResponseModel> {
    return this._HttpMainService.get(
      this.baseUrl + `v1/introducers/current`,
      undefined,
      new HttpContext().set(HTTP_ERROR_RESPONSE, true)
    );
  }
  getIntroducerById(
    userId: string
  ): Observable<CreateIbInformationResponseModel> {
    return this._HttpMainService.get(this.baseUrl + `v1/introducers/${userId}`);
  }
  getIntroducerOptions(
    introducerUserId: string
  ): Observable<IntroducerOptionsResponseModel> {
    return this._HttpMainService.get(
      `/trade/v1/introducers/${introducerUserId}/options`,
      {}
    );
  }
  changeIntroducerOptions(
    introducerUserId: string,
    model: IntroducerOptionsResponseModel
  ): Observable<IntroducerOptionsResponseModel> {
    const url = `/trade/v1/introducers/${introducerUserId}/options`;
    return this._HttpMainService.put(url, model);
  }
}
